/**
 * This file turns inline i18n placeholder tokens in rendered HTML text (⟦i18n:some.key⟧)
 * into clickable, inline-editable <span> elements, tracks edits, and batches saves back
 * to the server.
 *
 * How it works:
 * 1) Token detection & hydration
 *    - TOKEN_RE matches tokens inside text nodes.
 *    - hydrateTextNodes(root) walks the DOM under `root`, finds text nodes containing
 *      tokens, and replaces each token with a <span class="i18n-edit"> while preserving
 *      surrounding text.
 *    - Each span stores metadata:
 *        - data-i18n-key: translation key
 *        - data-i18n-original: original text at hydration time (for change detection)
 *    - The display value is resolved from js_localized[key]
 *
 * 2) Inline editing UX & dirty tracking
 *    - attachI18nEditHandlers(root) sets up event delegation on `root`:
 *        - Ctrl/Cmd + Click: enables contenteditable=true, focuses the span, and adds a
 *          green outline to indicate edit mode.
 *        - input: when actively editing, markDirty(key, value) stores the new text in
 *          dirtyI18n (a Map keyed by translation key).
 *        - blur: disables editing, restores default outline, and marks dirty if the value
 *          differs from the original.
 *
 * 3) Save bar UI + persistence
 *    - ensureI18nSaveBar() creates a small fixed “Unsaved changes / Save” bar on demand.
 *    - markDirty() shows the bar and updates the count; clearDirty() hides it.
 *    - saveDirtyTranslations() batches all dirty entries and sends them via:
 *        ConnectApi('Translate', 'saveMultiple', [language, payload])
 *      On success, it updates each edited element’s data-i18n-original so subsequent edits
 *      compare against the latest saved value.
 *    - cssEscape() is used to safely build attribute selectors when updating originals.
 *
 * 4) Auto-hydration for dynamically injected DOM
 *    - bindMutationObserver() starts a MutationObserver that watches for added elements.
 *    - Added nodes containing i18n tokens are queued in `pendingNodes` and processed after
 *      a short debounce.
 *    - During hydration, the observer is temporarily disconnected (isHydrating) to avoid
 *      recursively reacting to the DOM changes caused by replacements.
 *
 * Typical usage:
 *   - Call hydrateTextNodes(document) once after initial render.
 *   - Call bindMutationObserver() if your UI adds content dynamically (SPA, AJAX, etc.).
 */

import { ConnectApi } from '@js/plugins/connectApi';
import { documentReady, eraseCookie, getCookie, overrideTranslationFunctions, qs, setCookie, sleep } from '@js/utils/toolbox';


const TOKEN_RE = /⟦i18n:([^⟧]+)⟧/g;
const dirtyI18n = new Map();
let i18nHandlersAttached = false;
const cookieName = 'inlineTranslation';

/**
 * Enable inline translation editing
 */
export function enableInlineTranslationEditing () {
	setCookie(cookieName, '1', 7);
}

/**
 * Disable inline translation editing
 */
export function disableInlineTranslationEditing () {
	eraseCookie(cookieName);
}

/**
 * Check if inline translation editing is enabled
 * @returns {boolean}
 */
export function isInlineTranslationEditingEnabled () {
	return getCookie(cookieName) === '1';
}

/**
 * Initialize inline translation editing
 */
export function initializeInlineTranslationEditing () {
	hydrateTextNodes();
	bindMutationObserver();
	overrideTranslationFunctions();
}

/**
 * Escape CSS selector values
 */
function cssEscape (value) {
	return (globalThis.CSS && CSS.escape)
		? CSS.escape(value)
		: value.replace(/["\\]/g, '\\$&');
}

/**
 * Create editable translation element
 */
function getEditableElement (key, value) {
	const span = document.createElement('span');
	span.className = 'i18n-edit';
	span.style.outline = 'dashed 1px #888';
	span.style.cursor = 'pointer';
	span.style.pointerEvents = 'auto';
	span.setAttribute('contenteditable', 'false');
	span.setAttribute('data-i18n-key', key);
	span.setAttribute('data-i18n-original', String(value));
	span.setAttribute('title', 'Ctrl+Click to edit translation');
	span.textContent = String(value);
	return span;
}

/**
 * Replace translation tokens in text nodes with editable elements
 */
export function hydrateTextNodes (root = document) {
	const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
	const toReplace = [];

	// Find all text nodes with translation tokens
	while (walker.nextNode()) {
		const n = walker.currentNode;
		if (TOKEN_RE.test(n.nodeValue)) {
			toReplace.push(n);
		}
		TOKEN_RE.lastIndex = 0;
	}

	// Collect unique translation keys
	const keysToFetch = new Set();
	for (const textNode of toReplace) {
		const tpl = textNode.nodeValue;
		let match;
		while ((match = TOKEN_RE.exec(tpl)) !== null) {
			keysToFetch.add(match[1]);
		}
		TOKEN_RE.lastIndex = 0;
	}

	// Replace text nodes with editable elements
	for (const textNode of toReplace) {
		const tpl = textNode.nodeValue;
		const frag = document.createDocumentFragment();
		let lastIndex = 0;
		let match;
		TOKEN_RE.lastIndex = 0;
		while ((match = TOKEN_RE.exec(tpl)) !== null) {
			// Add preceding text
			if (match.index > lastIndex) {
				frag.appendChild(document.createTextNode(tpl.slice(lastIndex, match.index)));
			}
			// Add editable element
			const key = match[1];
			const val = globalThis.js_localized[key] ?? key;
			frag.appendChild(getEditableElement(key, val));
			lastIndex = TOKEN_RE.lastIndex;
		}
		// Add remaining text
		if (lastIndex < tpl.length) {
			frag.appendChild(document.createTextNode(tpl.slice(lastIndex)));
		}

		if (!textNode.parentNode) {
			console.log('no parent node for', textNode);
			continue;
		}

		textNode.parentNode.replaceChild(frag, textNode);
	}

	attachI18nEditHandlers(root);
}

/**
 * Attach event handlers for translation editing
 */
function attachI18nEditHandlers (root) {
	if (root === document && i18nHandlersAttached) {
		return;
	}
	if (root === document) {
		i18nHandlersAttached = true;
	}

	ensureI18nSaveBar();

	// Enable editing mode on Ctrl+Click
	root.addEventListener('click', (e) => {
		const el = e.target;
		if (!(el instanceof HTMLElement) || !el.classList.contains('i18n-edit')) {
			return;
		}

		// Only activate editing on Ctrl/Cmd + Click
		if (e.ctrlKey || e.metaKey) {
			e.preventDefault();
			e.stopPropagation();

			// Make editable and focus
			el.setAttribute('contenteditable', 'true');
			el.focus();

			// Add visual indicator that editing is active
			el.style.outline = 'solid 2px #4CAF50';
		}
	}, true);

	// Track edits on input (only if contenteditable is true)
	root.addEventListener('input', (e) => {
		const el = e.target;
		if (!(el instanceof HTMLElement) || !el.classList.contains('i18n-edit')) {
			return;
		}

		if (el.getAttribute('contenteditable') !== 'true') {
			return;
		}

		const key = el.dataset.i18nKey;
		if (!key) {
			return;
		}

		const value = el.textContent ?? '';
		markDirty(key, value);
	});

	// Mark dirty on blur if changed, and disable editing
	root.addEventListener('blur', (e) => {
		const el = e.target;
		if (!(el instanceof HTMLElement) || !el.classList.contains('i18n-edit')) {
			return;
		}

		if (el.getAttribute('contenteditable') !== 'true') {
			return;
		}

		const key = el.dataset.i18nKey;
		if (!key) {
			return;
		}

		const original = el.dataset.i18nOriginal ?? '';
		const value = el.textContent ?? '';
		if (value !== original) {
			markDirty(key, value);
		}

		// Disable editing mode
		el.setAttribute('contenteditable', 'false');
		el.style.outline = 'dashed 1px #888';
	}, true);

	// Save button handler
	const btn = document.getElementById('i18n-savebtn');
	btn?.addEventListener('click', saveDirtyTranslations);
}


/**
 * Create or get the save bar UI
 */
function ensureI18nSaveBar () {
	let bar = document.getElementById('i18n-savebar');
	if (bar) {
		return bar;
	}

	bar = document.createElement('div');
	bar.id = 'i18n-savebar';
	Object.assign(bar.style, {
		position: 'fixed',
		right: '16px',
		bottom: '16px',
		zIndex: '99999',
		display: 'none',
		gap: '8px',
		padding: '10px 12px',
		background: 'rgba(0,0,0,0.75)',
		borderRadius: '10px',
		color: '#fff',
		fontSize: '14px',
		alignItems: 'center'
	});

	const status = document.createElement('span');
	status.id = 'i18n-savebar-status';
	status.textContent = 'Unsaved changes';

	const btn = document.createElement('button');
	btn.id = 'i18n-savebtn';
	btn.type = 'button';
	btn.textContent = 'Save';
	Object.assign(btn.style, {
		cursor: 'pointer',
		padding: '6px 10px',
		borderRadius: '8px',
		border: '0'
	});

	bar.appendChild(status);
	bar.appendChild(btn);
	document.body.appendChild(bar);

	return bar;
}

function showSaveBar (text = 'Unsaved changes') {
	const bar = ensureI18nSaveBar();
	bar.style.display = 'flex';
	const status = document.getElementById('i18n-savebar-status');
	if (status) {
		status.textContent = text;
	}
}

function hideSaveBar () {
	const bar = document.getElementById('i18n-savebar');
	if (bar) {
		bar.style.display = 'none';
	}
}

function markDirty (key, value) {
	dirtyI18n.set(key, value);
	showSaveBar(`Unsaved changes (${dirtyI18n.size})`);
}

function clearDirty () {
	dirtyI18n.clear();
	hideSaveBar();
}

/**
 * Save all dirty translations to the server
 */
async function saveDirtyTranslations () {
	if (dirtyI18n.size === 0) {
		return;
	}

	showSaveBar('Saving…');

	const payload = Array.from(dirtyI18n.entries()).map(([key, text]) => ({
		key,
		text
	}));

	try {
		const response = await ConnectApi('Translate', 'saveMultiple', [globalThis.js_params.language, payload]);

		// Update originals so further edits track correctly
		for (const { key, text } of payload) {
			document.querySelectorAll(`.i18n-edit[data-i18n-key="${cssEscape(key)}"]`)
				.forEach((el) => {
					el.dataset.i18nOriginal = text;
				});
		}

		if (response?.success) {
			showSaveBar(`Saved successfully, updated: ${response.updated + response.inserted} entries`);
			await sleep(3000);
		} else {
			showSaveBar('Save failed — try again');
			return;
		}

		clearDirty();
	} catch (err) {
		console.error(err);
		showSaveBar('Save failed — check console');
	}
}

const pendingNodes = new Set();
let debounceTimer = null;
let isHydrating = false;

/**
 * Check if node contains i18n tokens
 */
function containsI18nTokens (node) {
	if (!node.textContent) {
		return false;
	}

	TOKEN_RE.lastIndex = 0;
	const result = TOKEN_RE.test(node.textContent);
	TOKEN_RE.lastIndex = 0;
	return result;
}

const mo = new MutationObserver((muts) => {
	if (isHydrating) {
		return;
	}

	for (const m of muts) {
		for (const n of m.addedNodes) {
			if (n.nodeType === 1 && containsI18nTokens(n)) {
				pendingNodes.add(n);
			}
		}
	}

	if (pendingNodes.size > 0) {
		clearTimeout(debounceTimer);
		debounceTimer = setTimeout(processPendingNodes, 100);
	}
});
/**
 * Process all pending nodes that need hydration
 */
function processPendingNodes () {
	if (pendingNodes.size === 0 || isHydrating) {
		return;
	}

	isHydrating = true;
	mo.disconnect();

	try {
		for (const node of pendingNodes) {
			if (document.contains(node)) {
				hydrateTextNodes(node);
			}
		}
	} finally {
		pendingNodes.clear();
		isHydrating = false;
		mo.observe(document.documentElement, { childList: true, subtree: true });
	}
}

/**
 * Mutation observer for auto-hydrating dynamically added content
 */
export function bindMutationObserver () {
	mo.observe(document.documentElement, { childList: true, subtree: true });
}


documentReady(() => {
	const editToggle = qs('#debug-menu-translation-edit-toggle');
	if (!editToggle) {
		return;
	}
	editToggle.addEventListener('change', (e) => {
		if (e.target.checked) {
			enableInlineTranslationEditing();
		} else {
			disableInlineTranslationEditing();
		}
		location.reload();
	});
	if (isInlineTranslationEditingEnabled()) {
		initializeInlineTranslationEditing();
	}
});
